import { RRule } from 'rrule'

import { Schedule } from '@/core/api/entities/schedule'

import { toLocalISOString } from './shared'
import { CalendarEvent } from '../context/Data'
import { COLOR_PALETTE } from '../context/Data'

const getColorFromString = (str: string): string => {
	let hash = 0
	for (let i = 0; i < str.length; i++) {
		hash = str.charCodeAt(i) + ((hash << 5) - hash)
		hash = hash & hash
	}
	const index = Math.abs(hash) % COLOR_PALETTE.length
	return COLOR_PALETTE[index]
}

export const convertTimeslotsToEvents = (
	scheduleDetails: Schedule,
	playlistMap?: Map<number, string>
): CalendarEvent[] => {
	const events: CalendarEvent[] = []

	scheduleDetails.timeslots.forEach((timeslot, index) => {
		// Check if it's a smart slot
		const isSmart =
			timeslot.type === 'weather' || timeslot.type === 'occupation'

		const playlistIds =
			timeslot.timeslotContents.length > 0 &&
			timeslot.timeslotContents[0].playlists &&
			timeslot.timeslotContents[0].playlists.length > 0
				? timeslot.timeslotContents[0].playlists
						.map((p) => p.id.toString())
						.sort()
						.join('|')
				: `Event ${index + 1}`

		const color = getColorFromString(playlistIds)

		const playlistNames =
			timeslot.timeslotContents.length > 0 &&
			timeslot.timeslotContents[0].playlists &&
			timeslot.timeslotContents[0].playlists.length > 0
				? timeslot.timeslotContents[0].playlists
						.map((p) => {
							const name =
								playlistMap?.get(p.id) || p.title || `Playlist ${p.id}`
							return name
						})
						.join(' + ')
				: 'Regular Slot'

		// For smart slots use timeslot.name, for regular slots use generated playlistNames
		const title = isSmart
			? timeslot.name || `Slot ${index + 1}`
			: playlistNames || timeslot.name

		// Parse VEVENT string to extract rrule information
		const veventLines = timeslot.vevent.split('\n')
		let dtstart = ''
		let dtend = ''
		let rruleStr = ''
		let duration = ''
		// REMOVED: exdateStr - not compatible with legacy apps

		veventLines.forEach((line) => {
			if (line.startsWith('DTSTART:')) {
				dtstart = line.substring('DTSTART:'.length)
			} else if (line.startsWith('DTEND:')) {
				dtend = line.substring('DTEND:'.length)
			} else if (line.startsWith('RRULE:')) {
				rruleStr = line.substring('RRULE:'.length)
			} else if (line.startsWith('DURATION:')) {
				duration = line.substring('DURATION:'.length)
			}
			// REMOVED: EXDATE parsing
		})

		const dtstartClean = dtstart.replace(/Z$/, '')

		const year = parseInt(dtstartClean.substring(0, 4))
		const month = parseInt(dtstartClean.substring(4, 6)) - 1 // JS months are 0-indexed
		const day = parseInt(dtstartClean.substring(6, 8))
		const hour = parseInt(dtstartClean.substring(9, 11))
		const minute = parseInt(dtstartClean.substring(11, 13))
		const second = parseInt(dtstartClean.substring(13, 15))

		const startDate = new Date(year, month, day, hour, minute, second)

		// Calculate duration based on DTEND or DURATION
		let durationHours = 0
		let durationMinutes = 0
		let endDate = startDate

		if (dtend) {
			const dtendClean = dtend.replace(/Z$/, '')

			const endYear = parseInt(dtendClean.substring(0, 4))
			const endMonth = parseInt(dtendClean.substring(4, 6)) - 1
			const endDay = parseInt(dtendClean.substring(6, 8))
			const endHour = parseInt(dtendClean.substring(9, 11))
			const endMinute = parseInt(dtendClean.substring(11, 13))
			const endSecond = parseInt(dtendClean.substring(13, 15))

			endDate = new Date(
				endYear,
				endMonth,
				endDay,
				endHour,
				endMinute,
				endSecond
			)

			const durationMs = endDate.getTime() - startDate.getTime()
			durationHours = Math.floor(durationMs / (1000 * 60 * 60))
			durationMinutes = Math.floor(
				(durationMs % (1000 * 60 * 60)) / (1000 * 60)
			)
		} else if (duration) {
			// Parse duration (format: PTxH or PTxHxM)
			const hourMatch = duration.match(/(\d+)H/)
			const minuteMatch = duration.match(/(\d+)M/)
			if (hourMatch) durationHours = parseInt(hourMatch[1])
			if (minuteMatch) durationMinutes = parseInt(minuteMatch[1])
			endDate = new Date(
				startDate.getTime() +
					durationHours * 60 * 60 * 1000 +
					durationMinutes * 60 * 1000
			)
		}

		// Get playlists and volume from first timeslot_content
		const playlists: Array<{
			id: number
			percentage: number
		}> =
			timeslot.timeslotContents.length > 0 &&
			timeslot.timeslotContents[0].playlists &&
			timeslot.timeslotContents[0].playlists.length > 0
				? timeslot.timeslotContents[0].playlists.map((p) => ({
						id: p.id,
						percentage: p.percentage || 100,
					}))
				: []

		const volume =
			timeslot.timeslotContents.length > 0
				? timeslot.timeslotContents[0].metadata.volume
				: 100

		// Parse rrule to determine repeat days and extract UNTIL date
		const repeatDays: string[] = []
		let untilDate: string | undefined = undefined
		if (rruleStr) {
			const bydayMatch = rruleStr.match(/BYDAY=([^;]+)/)
			if (bydayMatch) {
				const days = bydayMatch[1].split(',')
				const dayMap: Record<string, string> = {
					MO: 'monday',
					TU: 'tuesday',
					WE: 'wednesday',
					TH: 'thursday',
					FR: 'friday',
					SA: 'saturday',
					SU: 'sunday',
				}
				days.forEach((day) => {
					if (dayMap[day]) {
						repeatDays.push(dayMap[day])
					}
				})

				// Check if all 7 days are present
				if (repeatDays.length === 7) {
					repeatDays.length = 0
					repeatDays.push('everyday')
				}
			} else {
				// If there's an RRULE but no BYDAY, it's likely a DAILY recurrence
				if (rruleStr.includes('FREQ=DAILY')) {
					repeatDays.push('everyday')
				}
			}

			// Extract UNTIL date from RRULE if present
			const untilMatch = rruleStr.match(/UNTIL=([^;]+)/)
			if (untilMatch) {
				const untilStr = untilMatch[1].replace(/Z$/, '')

				const untilYear = parseInt(untilStr.substring(0, 4))
				const untilMonth = parseInt(untilStr.substring(4, 6)) - 1
				const untilDay = parseInt(untilStr.substring(6, 8))
				const untilHour = parseInt(untilStr.substring(9, 11)) || 0
				const untilMinute = parseInt(untilStr.substring(11, 13)) || 0
				const untilSecond = parseInt(untilStr.substring(13, 15)) || 0

				const untilDateObj = new Date(
					untilYear,
					untilMonth,
					untilDay,
					untilHour,
					untilMinute,
					untilSecond
				)
				untilDate = toLocalISOString(untilDateObj)
			}
		}

		// Parse rrule string for FullCalendar
		let rruleObj: any = undefined
		if (rruleStr) {
			// Extract byweekday from parsed repeatDays
			const weekdayMap: Record<string, any> = {
				monday: (RRule as any).MO,
				tuesday: (RRule as any).TU,
				wednesday: (RRule as any).WE,
				thursday: (RRule as any).TH,
				friday: (RRule as any).FR,
				saturday: (RRule as any).SA,
				sunday: (RRule as any).SU,
			}

			const byweekday =
				repeatDays[0] === 'everyday'
					? [
							(RRule as any).MO,
							(RRule as any).TU,
							(RRule as any).WE,
							(RRule as any).TH,
							(RRule as any).FR,
							(RRule as any).SA,
							(RRule as any).SU,
						]
					: repeatDays.map((day) => weekdayMap[day]).filter(Boolean)

			rruleObj = {
				freq: 'weekly',
				byweekday: byweekday.map((day: any) => day.weekday),
				dtstart: toLocalISOString(startDate),
			}

			// Add until date if present (for "this and following" deletion)
			if (untilDate) {
				rruleObj.until = untilDate
			}
		}

		// For recurring events, don't include start/end (only rrule/duration)
		// For one-time events, include start/end
		const event: CalendarEvent = {
			id: `timeslot-${timeslot.id}`,
			timeslotId: timeslot.id,
			timeslotContentId:
				!isSmart && timeslot.timeslotContents.length > 0
					? timeslot.timeslotContents[0].id
					: undefined,
			title: title,
			description:
				repeatDays.length > 0
					? `Repeats on: ${repeatDays.join(', ')}`
					: isSmart
						? `Smart ${timeslot.type} slot`
						: 'One-time slot',
			start: rruleObj ? (startDate as any) : startDate,
			end: rruleObj ? (endDate as any) : endDate,
			color: color,
			backgroundColor: color,
			playlists: isSmart ? undefined : playlists,
			repeatDays: repeatDays,
			slotType: isSmart ? 'smart' : 'regular',
			smartSlotConfig: isSmart
				? {
						variant: timeslot.type as 'weather' | 'occupation',
						condition: timeslot.type as 'weather' | 'occupation',
					}
				: undefined,
			timeslotName: title,
			timeslotContents: isSmart
				? timeslot.timeslotContents.map((content) => ({
						id: content.id,
						type: content.type,
						volume: content.metadata?.volume || 100,
						...(content.metadata?.minPercentage !== undefined
							? { min_percentage: content.metadata.minPercentage }
							: {}),
						...(content.metadata?.maxPercentage !== undefined
							? { max_percentage: content.metadata.maxPercentage }
							: {}),
						playlists:
							content.playlists?.map((p) => ({
								id: p.id,
								percentage: p.percentage || 100,
							})) || [],
					}))
				: undefined,
			gradientColors: isSmart
				? (() => {
						const allPlaylistIds = new Set<number>()
						timeslot.timeslotContents.forEach((content) => {
							content.playlists?.forEach((p) => allPlaylistIds.add(p.id))
						})

						const uniqueIds = Array.from(allPlaylistIds).slice(0, 2)
						if (uniqueIds.length >= 2) {
							return [
								getColorFromString(uniqueIds[0].toString()),
								getColorFromString(uniqueIds[1].toString()),
							]
						} else if (uniqueIds.length === 1) {
							const id = uniqueIds[0].toString()
							return [getColorFromString(id), getColorFromString(id + '_alt')]
						} else {
							return [color, getColorFromString(title + '_alt')]
						}
					})()
				: undefined,
			...(rruleObj ? { rrule: rruleObj } : {}), // Only for recurring events
			...(rruleObj && (durationHours > 0 || durationMinutes > 0)
				? {
						duration: {
							milliseconds:
								durationHours * 60 * 60 * 1000 + durationMinutes * 60 * 1000,
						},
					}
				: {}),
			...(rruleObj
				? {
						startTime: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`,
					}
				: {}),
		}

		// Add volume property (extending CalendarEvent)
		;(event as any).volume = volume

		events.push(event)
	})

	return events
}
